import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../services/api';
import { Search, FileText, CheckSquare, Wallet, X, ArrowRight } from 'lucide-react';

interface SearchNote { id: number; title: string; preview: string; updated_at: string; }
interface SearchTask { id: number; title: string; description: string; status: string; priority: string; }
interface SearchBudget { id: number; amount: number; type: string; description: string; date: string; category_name: string | null; category_color: string | null; }
interface SearchResults { notes: SearchNote[]; tasks: SearchTask[]; budget: SearchBudget[]; }

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: 'notes' | 'tasks' | 'budget', id?: number) => void;
}

export function GlobalSearch({ isOpen, onClose, onNavigate }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/search?q=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      if (data.success) setResults(data.data);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (v: string) => {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!isOpen) return null;

  const total = (results?.notes.length ?? 0) + (results?.tasks.length ?? 0) + (results?.budget.length ?? 0);
  const fmt = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl mx-4 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700">
          <Search size={18} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => handleChange(e.target.value)}
            placeholder="Поиск по заметкам, задачам, бюджету..."
            className="flex-1 bg-transparent text-white placeholder-slate-500 text-base outline-none"
          />
          {query && (
            <button onClick={() => { setQuery(''); setResults(null); }} className="text-slate-500 hover:text-slate-300">
              <X size={16} />
            </button>
          )}
          <kbd className="text-xs text-slate-500 bg-slate-800 border border-slate-600 rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="p-6 text-center text-slate-500 text-sm">Поиск...</div>
          )}

          {!loading && query.length >= 2 && results && total === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm">Ничего не найдено</div>
          )}

          {!loading && results && results.notes.length > 0 && (
            <div className="px-2 py-2">
              <p className="px-3 py-1 text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <FileText size={11} /> Заметки
              </p>
              {results.notes.map(note => (
                <button key={note.id}
                  onClick={() => { onNavigate('notes', note.id); onClose(); }}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-slate-800 flex items-start gap-3 group transition-colors">
                  <FileText size={15} className="text-indigo-400 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{note.title || 'Без названия'}</p>
                    {note.preview && <p className="text-xs text-slate-500 truncate mt-0.5">{note.preview}</p>}
                  </div>
                  <ArrowRight size={13} className="text-slate-600 group-hover:text-slate-400 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}

          {!loading && results && results.tasks.length > 0 && (
            <div className="px-2 py-2 border-t border-slate-800">
              <p className="px-3 py-1 text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <CheckSquare size={11} /> Задачи
              </p>
              {results.tasks.map(task => (
                <button key={task.id}
                  onClick={() => { onNavigate('tasks', task.id); onClose(); }}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-slate-800 flex items-start gap-3 group transition-colors">
                  <CheckSquare size={15} className="text-violet-400 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{task.title}</p>
                    {task.description && <p className="text-xs text-slate-500 truncate mt-0.5">{task.description}</p>}
                  </div>
                  <ArrowRight size={13} className="text-slate-600 group-hover:text-slate-400 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}

          {!loading && results && results.budget.length > 0 && (
            <div className="px-2 py-2 border-t border-slate-800">
              <p className="px-3 py-1 text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Wallet size={11} /> Бюджет
              </p>
              {results.budget.map(entry => (
                <button key={entry.id}
                  onClick={() => { onNavigate('budget', entry.id); onClose(); }}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-slate-800 flex items-start gap-3 group transition-colors">
                  <div className="w-3.5 h-3.5 rounded-full shrink-0 mt-1"
                    style={{ background: entry.category_color || (entry.type === 'income' ? '#22c55e' : '#ef4444') }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">
                      <span className={entry.type === 'income' ? 'text-green-400' : 'text-red-400'}>
                        {entry.type === 'income' ? '+' : '−'}{fmt(entry.amount)} ₽
                      </span>
                      {entry.description && <span className="text-slate-400 ml-1.5 font-normal">{entry.description}</span>}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{entry.date}{entry.category_name ? ` · ${entry.category_name}` : ''}</p>
                  </div>
                  <ArrowRight size={13} className="text-slate-600 group-hover:text-slate-400 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}

          {!loading && !results && query.length < 2 && (
            <div className="px-4 py-5 text-sm text-slate-600">
              Введите не менее 2 символов для поиска
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-800 flex items-center gap-4 text-xs text-slate-600">
          <span>↵ открыть</span>
          <span>Esc закрыть</span>
          {results && total > 0 && <span className="ml-auto">{total} результат{total !== 1 ? (total < 5 ? 'а' : 'ов') : ''}</span>}
        </div>
      </div>
    </div>
  );
}
